import React, { useState } from "react";
import { DateRange } from "react-day-picker";
import { format } from "date-fns";
import { CalendarIcon, Check } from "lucide-react";

import { cn } from "../utils/cn";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Separator } from "./ui/separator";

export type DateFilterValue = {
  /** One of the getDateRange presets, or "custom". */
  preset: string;
  from?: Date;
  to?: Date;
};

export const DATE_PRESETS = [
  { value: "", label: "All time" },
  { value: "today", label: "Today" },
  { value: "thisWeek", label: "This week" },
  { value: "last3Months", label: "Last 3 months" },
];

type Props = {
  value: DateFilterValue;
  onChange: (value: DateFilterValue) => void;
  className?: string;
};

/**
 * Preset picker with an optional custom range. Emits either a preset key (fed
 * to getDateRange) or explicit from/to dates, so callers keep sending the same
 * from/to query params the API already expects.
 */
const DateRangeFilter = ({ value, onChange, className }: Props) => {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState<DateRange | undefined>(
    value.from ? { from: value.from, to: value.to } : undefined
  );

  const label =
    value.preset === "custom" && value.from
      ? `${format(value.from, "d MMM yyyy")}${
          value.to ? ` – ${format(value.to, "d MMM yyyy")}` : ""
        }`
      : DATE_PRESETS.find((preset) => preset.value === value.preset)?.label ??
        "All time";

  const applyPreset = (preset: string) => {
    setRange(undefined);
    onChange({ preset });
    setOpen(false);
  };

  const applyRange = (next: DateRange | undefined) => {
    setRange(next);
    if (next?.from && next?.to) {
      onChange({ preset: "custom", from: next.from, to: next.to });
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("h-9 justify-start gap-2 bg-background font-medium", className)}
        >
          <CalendarIcon className="size-4 shrink-0" />
          <span className="truncate">{label}</span>
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-auto p-0 font-manrope">
        <div className="p-1.5">
          {DATE_PRESETS.map((preset) => (
            <button
              key={preset.value || "all"}
              type="button"
              onClick={() => applyPreset(preset.value)}
              className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-[13px] font-medium transition-colors hover:bg-accent"
            >
              {preset.label}
              {value.preset === preset.value && (
                <Check className="size-4 text-primary" />
              )}
            </button>
          ))}
        </div>

        <Separator />

        <div className="p-1.5">
          <p className="px-3 pb-1 pt-2 text-[12px] font-semibold uppercase tracking-wide text-muted-foreground">
            Custom range
          </p>
          <Calendar
            mode="range"
            selected={range}
            onSelect={applyRange}
            numberOfMonths={1}
            disabled={{ after: new Date() }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DateRangeFilter;
